import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    IconButton,
    Typography,
    Select,
    MenuItem,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { getProjectList } from '../../lib/api/salesAction'; 

const ProjectModalConatiner = ({ open, onClose, onSelectSchedule }) => {
    const [projectType, setProjectType] = useState('CHANCE'); 
    const [searchType, setSearchType] = useState('title'); 
    const [keyword, setKeyword] = useState(''); 

    const [projects, setProjects] = useState([]); 
    const [loading, setLoading] = useState(false);  
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const fetchProjects = useCallback(async () => {
        if (!open) return; 
        
        try {
            setLoading(true);
            const localUser = localStorage.getItem('user');
            let usrUuid = '';

            if (localUser) {
                const parsedUser = JSON.parse(localUser);
                usrUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
            }

            const queryParams = {
                projectType: projectType === 'CHANCE' ? '' : projectType,
                searchType: searchType,
                selectedYear: selectedYear,
                keyword: keyword.trim()
            };
            
            const response = await getProjectList(usrUuid, queryParams);
            if (response && response.data && Array.isArray(response.data.list)) {
                setProjects(response.data.list);
            } else {
                setProjects([]);
            }
        } catch (error) {
            console.error('프로젝트 목록을 불러오는 중 오류가 발생했습니다:', error);
        } finally {
            setLoading(false);
        }
    }, [open, projectType, searchType, selectedYear, keyword]);

    useEffect(() => {
        fetchProjects();
    }, [open]); 

    const handleSearch = () => {
        fetchProjects();
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            fullWidth 
            PaperProps={{
                sx: {
                    width: '900px',      
                    height: '700px',     
                    maxWidth: 'none',    
                    maxHeight: 'none',
                    borderRadius: '12px' 
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                    사업명 선택
                </Typography>
                <IconButton onClick={onClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        alignItems: 'center', 
                        gap: 1.5, 
                        p: 2, 
                        backgroundColor: '#ffffff', 
                        borderRadius: '8px', 
                        border: '1px solid #e9ecef', 
                        mb: 3,
                        flexWrap: 'nowrap'
                    }}
                >
                    {/* 🎯 년도 선택 영역 (현재 년도 자동 체크 및 +/- 동작 고정) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '4px', height: 42, minWidth: 130, backgroundColor: '#ffffff' }}>
                        <IconButton onClick={() => setSelectedYear(prev => (prev || new Date().getFullYear()) - 1)} size="small" sx={{ p: 0.75 }}>
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                            value={selectedYear}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                if (!isNaN(value)) {
                                    setSelectedYear(value);
                                } else if (e.target.value === '') {
                                    setSelectedYear('');
                                }
                            }}
                            variant="standard"
                            // 🎯 [정정] 구버전 MUI 스펙에 맞게 slotProps 대신 InputProps로 변경하여 경고 원천 차단
                            InputProps={{ disableUnderline: true }}
                            sx={{ width: '100%', '& input': { textAlign: 'center', fontWeight: 600, fontSize: '15px', p: 0 } }}
                        />
                        <IconButton onClick={() => setSelectedYear(prev => (prev || new Date().getFullYear()) + 1)} size="small" sx={{ p: 0.75 }}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Select
                        value={projectType}
                        onChange={(e) => setProjectType(e.target.value)}
                        size="small"
                        sx={{ minWidth: 130, height: 42 }}
                    >
                        <MenuItem value="CHANCE">포캐스팅</MenuItem>
                        <MenuItem value="N">프로젝트</MenuItem>
                        <MenuItem value="M">유지보수</MenuItem>
                    </Select>
                    <Select
                        value={searchType}
                        onChange={(e) => setSearchType(e.target.value)}
                        size="small"
                        sx={{ minWidth: 120, height: 42 }}
                    >
                        <MenuItem value="title">사업명</MenuItem>
                        <MenuItem value="manager">담당자</MenuItem> 
                    </Select>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="검색어를 입력해 주세요"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        sx={{ '& .MuiOutlinedInput-root': { height: 42 } }}
                    />
                    <Button 
                        variant="contained" 
                        onClick={handleSearch}
                        startIcon={<SearchIcon />}
                        sx={{ backgroundColor: '#228be6', '&:hover': { backgroundColor: '#1c7ed6' }, minWidth: 90, height: 42, boxShadow: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        검색
                    </Button>
                </Box>


                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#495057', px: 0.5 }}>
                    선택 가능한 프로젝트 목록
                </Typography>
                
                <Box sx={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e9ecef', maxHeight: 420, overflowY: 'auto', position: 'relative' }}>
                    
                    <Box 
                        sx={{ 
                            position: 'sticky', 
                            top: 0, 
                            zIndex: 2, 
                            backgroundColor: '#f1f3f5', 
                            borderBottom: '2px solid #dee2e6',
                            px: 2.5, 
                            py: 1.5 
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#495057', fontSize: '0.8rem' }}>매출월</Typography>
                            </Grid>
                            <Grid item xs={7}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#495057', fontSize: '0.8rem' }}>사업명</Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#495057', fontSize: '0.8rem' }}>담당자</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                    {Array.isArray(projects) && projects.length > 0 ? (
                        <List disablePadding>
                            {projects.map((project, index) => (
                                <React.Fragment key={project.PRJ_UUID || index}>
                                    <ListItem 
                                        button 
                                        onClick={() => onSelectSchedule(project)} 
                                        sx={{ 
                                            py: 1.5, 
                                            px: 2.5,
                                            borderBottom: '1px solid #f1f3f5',
                                            '&:hover': { backgroundColor: '#e7f5ff', cursor: 'pointer' }
                                        }}
                                    >
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={3}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {project.expDay}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {project.cstNm}
                                                </Typography>
                                            </Grid>                            
                                            <Grid item xs={2}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {project.crtNm}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    ) : (
                        !loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                                <Typography variant="body2" color="text.secondary">조회된 프로젝트 목록이 없습니다.</Typography>
                            </Box>
                        )
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectModalConatiner;
